import { useState, useRef, useCallback } from 'react';
import { useToast } from '@/hooks/use-toast';

// Type definitions for Web Speech API
interface SpeechRecognitionEvent extends Event {
  resultIndex: number;
  results: SpeechRecognitionResultList;
}

interface SpeechRecognitionErrorEvent extends Event {
  error: string;
  message?: string;
}

interface SpeechRecognition extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  start(): void;
  stop(): void;
  abort(): void;
  onresult: ((event: SpeechRecognitionEvent) => void) | null;
  onerror: ((event: SpeechRecognitionErrorEvent) => void) | null;
  onend: (() => void) | null;
  onstart: (() => void) | null;
}

declare global {
  interface Window {
    SpeechRecognition: new () => SpeechRecognition;
    webkitSpeechRecognition: new () => SpeechRecognition;
  }
}

export const useBrowserSpeechToText = (onTranscript?: (text: string) => void) => {
  const { toast } = useToast();
  const [isRecording, setIsRecording] = useState(false);
  const [interimTranscript, setInterimTranscript] = useState('');
  const recognitionRef = useRef<SpeechRecognition | null>(null);
  const finalTranscriptRef = useRef('');

  const isSupported = typeof window !== 'undefined' && 
    ('SpeechRecognition' in window || 'webkitSpeechRecognition' in window);

  const startRecording = useCallback(() => {
    if (!isSupported) {
      toast({
        title: 'Nicht unterstützt',
        description: 'Spracherkennung wird in diesem Browser nicht unterstützt. Bitte verwenden Sie Chrome oder Edge.',
        variant: 'destructive',
      });
      return;
    }

    try {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      const recognition = new SpeechRecognition();
      
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = 'de-DE';
      
      recognition.onstart = () => {
        setIsRecording(true);
        finalTranscriptRef.current = '';
        setInterimTranscript('');
      };
      
      recognition.onresult = (event: SpeechRecognitionEvent) => {
        let interim = '';
        let final = '';
        
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            final += transcript;
          } else {
            interim += transcript;
          }
        }
        
        if (final) {
          finalTranscriptRef.current += final;
          onTranscript?.(finalTranscriptRef.current);
        }
        setInterimTranscript(interim);
      };
      
      recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
        console.error('Speech recognition error:', event.error);
        
        if (event.error === 'not-allowed') {
          toast({
            title: 'Mikrofon-Zugriff verweigert',
            description: 'Bitte erlauben Sie den Zugriff auf das Mikrofon in Ihren Browser-Einstellungen.',
            variant: 'destructive',
          });
        } else if (event.error !== 'aborted') {
          toast({
            title: 'Spracherkennung fehlgeschlagen',
            description: 'Bitte versuchen Sie es erneut.',
            variant: 'destructive',
          });
        }
        
        setIsRecording(false);
      };
      
      recognition.onend = () => {
        setIsRecording(false);
      };
      
      recognitionRef.current = recognition;
      recognition.start();
      
    } catch (error) {
      console.error('Speech recognition error:', error);
      toast({
        title: 'Fehler',
        description: 'Spracherkennung konnte nicht gestartet werden.',
        variant: 'destructive',
      });
    }
  }, [isSupported, onTranscript, toast]);

  const stopRecording = useCallback((): string => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      recognitionRef.current = null;
    }
    setIsRecording(false);
    setInterimTranscript('');
    return finalTranscriptRef.current;
  }, []);

  const toggleRecording = useCallback((): string => {
    if (isRecording) {
      return stopRecording();
    } else {
      startRecording();
      return '';
    }
  }, [isRecording, startRecording, stopRecording]);

  return {
    isRecording,
    isSupported,
    interimTranscript,
    startRecording,
    stopRecording,
    toggleRecording,
  };
};
